import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components/macro'
import SearchResultItem from 'components/ui/SearchResultItem'
import { t } from 'ttag'
import SearchResultTitle from 'components/ui/SearchResultTitle'
import SearchResultDescription from 'components/ui/SearchResultDescription'
import SearchResultListing from 'components/ui/SearchResultListing'
import SearchResultContent from 'components/ui/SearchResultContent'
import Button from 'components/ui/Button'
import { useDispatch, useSelector } from 'react-redux'
import { downloadDocumentFile, toggleDocumentsVisible } from 'store/actions/search'
import SearchResult from 'components/ui/SearchResult'
import SelectedFilters from 'components/SelectedFilters'
import CustomPagination from 'components/CustomPagination'
import ElementSave from 'components/ElementSave'
import SearchResultSelectedFilters from 'components/ui/SearchResultSelectedFilters'
import ExtendedSearchResultInfo from 'components/ui/ExtendedSearchResultInfo'
import InfoCol from 'components/ui/InfoCol'
import ResultInfo from 'components/ui/ResultInfo'
import SearchFooter from 'components/ui/SearchFooter'
import ResultActions from 'components/ui/ResultActions'
import ResultAction from 'components/ui/ResultAction'
import ResultActionIcon from 'components/ui/ResultActionIcon'
import ResultUrlContainer from 'components/ui/ResultUrlContainer'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { getResultUrl } from 'util/hash'
import { RESULTS_PER_PAGE } from 'store/constants/constants'
import breakpoints from 'assets/stylesheet'
import PartnersMap from 'components/PartnersMap'
import SpinnerContainer from 'components/ui/SpinnerContainer'
import { show } from 'redux-modal'

const StyledButton = styled(Button)`
  padding: 6px 16px;
  margin-top: 10px;
  width: auto;
  font-weight: normal;
  @media (max-width: ${breakpoints.bpPhonePx}) {
    font-size: 14px !important;
  }
`
const LocationResultsTitle = styled.h2`
  color: #4a4a4a !important;
  font-size: 22px !important;
  margin-top: 10px !important;
  margin-bottom: 15px !important;
  @media (max-width: ${breakpoints.bpPhonePx}) {
    font-size: 18px !important;
  }
`
const Link = styled.a`
  color: #003399;
  text-decoration: none;
`
const AttachmentSearchResultContent = styled(SearchResultContent)`
  padding: 12px;
`
const AttachmentContainer = styled.div`
  padding: 5px 0;
`
const AttachmentAction = styled.a`
  border-radius: 2px;
  background-color: #a6a6a6;
  text-decoration: none;
  color: #0f0f0f;
  font-family: 'Open Sans';
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  padding: 5px;
  cursor: pointer;
  margin-right: 27px;
  margin-top: 5px;
  display: inline-block;
`
const ResultTitle = styled.span`
  color: #003399;
  font-weight: bold;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  padding: 2px 0;
  padding-right: 21px;
`
const AttachmentResultTitle = styled(ResultTitle)`
  color: #4a4a4a;
`
const ResultSubtitle = styled.div`
  color: #4a4a4a;
  font-weight: bold;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 4px 0;
`
const AttachmentResultSubtitle = styled(ResultSubtitle)`
  color: #003399;
`
const ResultText = styled.div`
  color: #4a4a4a;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Loading = styled.div`
  position: relative !important;
  margin-top: 50px;
`
const ErrorText = styled.p`
  color: #f31100;
`
const AttachmentIcon = styled.i`
  font-size: 14px;
  color: #0f0f0f;
  margin-left: 20px;
  cursor: pointer;
`
const ToggleContentLabel = styled.b`
  display: block;
  padding: 10px 0 4px 0;
`
const ProjectSearchResultContainer = styled.div`
  display: flex;
  width: 100%;
  @media (max-width: ${breakpoints.bpDesktopPx}) {
    display: block;
  }
`
const ProjectSearchResultListingContainer = styled.div`
  width: 50%;
  @media (max-width: ${breakpoints.bpDesktopPx}) {
    width: 100%;
  }
`
const ResultMapContainer = styled.div`
  width: 50%;
  position: relative;
  @media (max-width: ${breakpoints.bpDesktopPx}) {
    width: 100%;
  }
`
const ProjectSearchResultListing = styled(SearchResultListing)`
  width: auto;
  @media (max-width: ${breakpoints.bpDesktopPx}) {
    display: block;
  }
`
const ProjectSearchResultItem = styled.li`
  width: 100%;
  @media (min-width: ${breakpoints.contentWrapLarge}) {
    width: 50%;
  }
  @media (max-width: ${breakpoints.bpDesktopPx}) {
    width: 100%;
  }
`
const LanguageList = styled.ul`
  padding-left: 18px;
  list-style-type: disc;
`
const InfoLink = styled.a`
  margin-left: 10px;
  cursor: pointer;
`

const handleProjectTranslation = (project, field) => {
  let value
  let languageIndex = 0
  if ('en' in project.translations && project.translations.en[field]) {
    value = project.translations.en[field]
  } else {
    while (!value && project.translations[Object.keys(project.translations)[languageIndex]]) {
      value = project.translations[Object.keys(project.translations)[languageIndex]][field]
      languageIndex += 1
    }
  }

  return value
}

// eslint-disable-next-line no-useless-escape
const handleFileName = path => path.replace(/^.*[\\\/]/, '')

const getAttachmentTitle = attachment => {
  if (attachment.attachment_type === 'file') {
    return attachment.name
  }
  return attachment.name ? attachment.name : attachment.link_url
}

function ProjectSearchResults({
  searchResults: {
    results = [],
    count,
    related_count: relatedCount,
    current,
    next,
    previous,
    total_pages: totalPages
  },
  documentSearchResults: {
    results: documentResults,
    count: documentCount,
    current: documentCurrent,
    next: documentNext,
    previous: documentPrevious,
    total_pages: documentTotalPages
  },
  isPending,
  error,
  isSidebarVisible,
  onPageChange,
  hasLocationPermission,
  hasLocationSearchResults,
  onExportToExcel,
  currentFilters,
  onGetResultUrl,
  resultHash,
  onMapImageDownload,
  isMapDownloadPending,
  isExcelExportPending
}) {
  const dispatch = useDispatch()
  const search = useSelector(state => state.search)
  const [showTableSaveModal, setShowTableSaveModal] = useState(false)
  const { isDocumentsVisible, isDocumentsPending } = search
  const cookies = document.cookie.split('; ')
  const authenticated = cookies.some(c => c.startsWith('keep_auth_token'))

  const handleFileDownload = result => {
    dispatch(
      downloadDocumentFile({
        id: result.id,
        fileName: handleFileName(result.file)
      })
    )
  }

  const getProjectToggleContent = result => (
    <div>
      <ToggleContentLabel>{t`Description:`}</ToggleContentLabel>
      {handleProjectTranslation(result, 'description')
        ? handleProjectTranslation(result, 'description')
        : '-'}
    </div>
  )

  const handleDocumentExport = data => {
    onExportToExcel(data, true)
  }

  const getAttachmentToggleContent = result => (
    <div>
      {result.type ? (
        <div>
          <ToggleContentLabel>{t`Type:`}</ToggleContentLabel>
          {result.type}
        </div>
      ) : (
        ''
      )}
      {result.description ? (
        <div>
          <ToggleContentLabel>{t`Description:`}</ToggleContentLabel>
          {result.description}
        </div>
      ) : (
        ''
      )}
      {result.data_source ? (
        <div>
          <ToggleContentLabel>{t`Data source:`}</ToggleContentLabel>
          {result.data_source}
        </div>
      ) : (
        ''
      )}
      {result.publication_date ? (
        <div>
          <ToggleContentLabel>{t`Publication date:`}</ToggleContentLabel>
          {result.publication_date}
        </div>
      ) : (
        ''
      )}
      {result.document_date ? (
        <div>
          <ToggleContentLabel>{t`Document date:`}</ToggleContentLabel>
          {result.document_date}
        </div>
      ) : (
        ''
      )}
      {result.languages && result.languages.length > 0 ? (
        <ToggleContentLabel>{t`Languages:`}</ToggleContentLabel>
      ) : (
        ''
      )}
      {result.languages && result.languages.length > 0 ? (
        <LanguageList>
          {result.languages.map(lan => (
            <li key={lan}>{lan}</li>
          ))}
        </LanguageList>
      ) : (
        ''
      )}
    </div>
  )

  let resultsComponent
  const loading =
    (isDocumentsVisible && (isDocumentsPending || isPending)) || (!isDocumentsVisible && isPending)

  if (isDocumentsVisible) {
    if (documentResults.length === 0) {
      resultsComponent = <b>{t`No results found.`}</b>
    } else {
      resultsComponent = (
        <>
          <SearchResultListing>
            {documentResults.map(result => (
              <SearchResultItem key={result.id}>
                <AttachmentSearchResultContent>
                  <SearchResult
                    toggleContent={getAttachmentToggleContent(result)}
                    truncateContent={false}
                  >
                    <AttachmentContainer>
                      <AttachmentResultTitle
                        title={`${getAttachmentTitle(result)} (${result.type})`}
                      >{`${getAttachmentTitle(result)} (${result.type})`}</AttachmentResultTitle>
                      <Link target="_blank" href={`/projects/${result.project}`}>
                        <AttachmentResultSubtitle>{result.acronym}</AttachmentResultSubtitle>
                      </Link>
                      <ResultText>{result.programme_title}</ResultText>
                      <div>
                        {result.attachment_type === 'file' ? (
                          <AttachmentAction onClick={() => handleFileDownload(result)}>
                            {t`Download`} <AttachmentIcon className="far fa-file-download" />
                          </AttachmentAction>
                        ) : (
                          <AttachmentAction
                            target="_blank"
                            rel="noopener noreferrer"
                            href={result.link_url}
                          >
                            {t`Visit`} <AttachmentIcon className="far fa-external-link" />
                          </AttachmentAction>
                        )}
                      </div>
                    </AttachmentContainer>
                  </SearchResult>
                </AttachmentSearchResultContent>
              </SearchResultItem>
            ))}
          </SearchResultListing>
          {documentCount > RESULTS_PER_PAGE && (
            <SearchFooter>
              <CustomPagination
                onPageChange={data => onPageChange(data, true)}
                count={documentCount}
                current={documentCurrent}
                next={documentNext}
                previous={documentPrevious}
                totalPages={documentTotalPages}
              />
            </SearchFooter>
          )}
        </>
      )
    }
  } else if (results.length === 0) {
    resultsComponent = <b>{t`No results found.`}</b>
  } else {
    const urlParams = new URLSearchParams(window.location.search)
    const hideResults = urlParams.get('hide-results')

    resultsComponent = (
      <>
        <ProjectSearchResultContainer>
          <ProjectSearchResultListingContainer>
            {!hideResults && (
              <ProjectSearchResultListing>
                {results.map(result => (
                  <ProjectSearchResultItem key={result.id}>
                    <SearchResultContent>
                      <SearchResult toggleContent={getProjectToggleContent(result)}>
                        <Link target="_blank" href={`/projects/${result.id}`}>
                          <ResultTitle>{result.acronym}</ResultTitle>
                        </Link>
                        <ResultSubtitle>{handleProjectTranslation(result, 'name')}</ResultSubtitle>
                        {result.programme && <ResultText>{result.programme.title}</ResultText>}
                      </SearchResult>
                    </SearchResultContent>
                  </ProjectSearchResultItem>
                ))}
              </ProjectSearchResultListing>
            )}
            {count > RESULTS_PER_PAGE && (
              <SearchFooter>
                <CustomPagination
                  onPageChange={data => onPageChange(data)}
                  count={count}
                  current={current}
                  next={next}
                  previous={previous}
                  totalPages={totalPages}
                />
              </SearchFooter>
            )}
          </ProjectSearchResultListingContainer>
          <ResultMapContainer>
            <PartnersMap isSidebarVisible={isSidebarVisible} />
          </ResultMapContainer>
        </ProjectSearchResultContainer>
      </>
    )
  }

  if (error) {
    resultsComponent = <ErrorText>{t`An unexpected error occurred.`}</ErrorText>
  }

  const urlParams = new URLSearchParams(window.location.search)
  const hideSearch = urlParams.get('hide-search')
  if (hideSearch) {
    return (
      <ResultMapContainer>
        <PartnersMap isSidebarVisible={isSidebarVisible} />
      </ResultMapContainer>
    )
  }

  return (
    <>
      {isSidebarVisible ? (
        <>
          <SearchResultTitle>
            {isDocumentsVisible ? (
              <span>{t`Search results - documents view`}</span>
            ) : (
              <span>{t`Search results - project view`}</span>
            )}
            <StyledButton
              id="pd_switch_to"
              onClick={() =>
                dispatch(
                  toggleDocumentsVisible({
                    isDocumentsVisible: !isDocumentsVisible
                  })
                )
              }
            >
              {isDocumentsVisible ? t`Switch to project view` : t`Switch to document view`}
            </StyledButton>
          </SearchResultTitle>
          <ExtendedSearchResultInfo>
            <InfoCol>
              {!isPending && !error && (
                <ResultInfo>{t`${count
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} projects, ${(relatedCount.docs || 0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} documents, 
                ${(relatedCount.partners || 0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} partners, ${(
                  relatedCount.partnerships || 0
                )
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} partnerships and 
                ${(relatedCount.programmes || 0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} programmes found`}</ResultInfo>
              )}
            </InfoCol>
            {isDocumentsVisible ? (
              <InfoCol>
                <SearchResultDescription>{t`
              Click on any of the buttons 'Download' or 'Visit' to get to the required documents.
              `}</SearchResultDescription>
              </InfoCol>
            ) : (
              <InfoCol>
                <SearchResultDescription>{t`
              Click on any entry below to get to the full data on a project and its partners,
              or export the table below to Excel and get aggregated data on all projects, partners and calls.
              `}</SearchResultDescription>
                <SearchResultDescription>{t`
              All the project-partner-call data in keep.eu is the responsibility of the programmes financing
              those projects, with the exception of the information of European Union macro-regional strategies,
              which is collected from these strategies' documents.
              `}</SearchResultDescription>
              </InfoCol>
            )}
          </ExtendedSearchResultInfo>
        </>
      ) : (
        <>
          <SearchResultTitle>
            {isDocumentsVisible ? (
              <span>{t`Search results - documents view`}</span>
            ) : (
              <span>{t`Search results - project view`}</span>
            )}
            <StyledButton
              onClick={() =>
                dispatch(
                  toggleDocumentsVisible({
                    isDocumentsVisible: !isDocumentsVisible
                  })
                )
              }
            >
              {isDocumentsVisible ? t`Switch to project view` : t`Switch to document view`}
            </StyledButton>
          </SearchResultTitle>
          <ExtendedSearchResultInfo>
            <InfoCol>
              {!isPending && !error && (
                <ResultInfo>{t`${count} projects, ${(relatedCount.docs || 0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} documents, 
                ${(relatedCount.partners || 0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} partners, ${(
                  relatedCount.partnerships || 0
                )
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} partnerships and 
                ${(relatedCount.programmes || 0)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} programmes found`}</ResultInfo>
              )}
              <SearchResultSelectedFilters>
                <SelectedFilters currentFilters={currentFilters} />
              </SearchResultSelectedFilters>
            </InfoCol>
            {isDocumentsVisible ? (
              <InfoCol>
                <SearchResultDescription>{t`
              Click on any of the buttons 'Download' or 'Visit' to get to the required documents.
              `}</SearchResultDescription>
              </InfoCol>
            ) : (
              <InfoCol>
                <SearchResultDescription>{t`
              Click on any entry below to get to the full data on a project and its partners,
              or export the table below to Excel and get aggregated data on all projects, partners and calls.
              `}</SearchResultDescription>
                <SearchResultDescription>{t`
              All the project-partner-call data in keep.eu is the responsibility of the programmes financing
              those projects, with the exception of the information of European Union macro-regional strategies,
              which is collected from these strategies' documents.
              `}</SearchResultDescription>
              </InfoCol>
            )}
          </ExtendedSearchResultInfo>
        </>
      )}

      {loading ? (
        <Loading>
          <LoadingSpinner showText />
        </Loading>
      ) : (
        <>
          {hasLocationPermission && hasLocationSearchResults && !isDocumentsVisible && (
            <LocationResultsTitle>{t`Projects near you`}</LocationResultsTitle>
          )}
          <ResultActions>
            {results.length > 0 && !isDocumentsVisible && (
              <>
                <ResultAction onClick={onMapImageDownload} id="pd_download_map">
                  {isMapDownloadPending ? t`Downloading map image` : t`Download map image`}{' '}
                  {isMapDownloadPending ? (
                    <SpinnerContainer>
                      <LoadingSpinner showText={false} />
                    </SpinnerContainer>
                  ) : (
                    <ResultActionIcon className="far fa-file-download" />
                  )}
                </ResultAction>
                <ResultAction onClick={onExportToExcel} id="pd_to_excel">
                  {t`Export to Excel`}{' '}
                  {isExcelExportPending ? (
                    <SpinnerContainer>
                      <LoadingSpinner showText={false} />
                    </SpinnerContainer>
                  ) : (
                    <ResultActionIcon className="far fa-file-excel" />
                  )}
                </ResultAction>
              </>
            )}
            {documentResults.length > 0 && isDocumentsVisible && (
              <ResultAction onClick={handleDocumentExport} id="d_to_excel">
                {t`Export to Excel`}{' '}
                {isExcelExportPending ? (
                  <SpinnerContainer>
                    <LoadingSpinner showText={false} />
                  </SpinnerContainer>
                ) : (
                  <ResultActionIcon className="far fa-file-excel" />
                )}
              </ResultAction>
            )}
            <>
              {resultHash === '' ? (
                <ResultAction onClick={onGetResultUrl} id="pd_get_result_url">
                  {t`Get result url`} <ResultActionIcon className="far fa-external-link" />
                </ResultAction>
              ) : (
                <>
                  <ResultUrlContainer
                    onClick={e => e.target.select()}
                    defaultValue={getResultUrl(resultHash)}
                  />
                  <InfoLink
                    target="_blank"
                    onClick={async () => {
                      const data = await fetch(
                        `/wp-json/wp/v2/pages?slug=url-get-results-url-how-to-shorten-the-urls`
                      )
                      const jsonData = await data.json()
                      const msg = jsonData[0].content.rendered
                      dispatch(show('infoModal', { message: msg }))
                    }}
                  >{t`How to shorten the URLs?`}</InfoLink>
                </>
              )}
            </>
            {authenticated && (
              <ResultAction
                onClick={() => setShowTableSaveModal(true)}
              >{t`Save table`}</ResultAction>
            )}
          </ResultActions>
          <ElementSave
            type="table"
            show={showTableSaveModal}
            isSidebarVisible={isSidebarVisible}
            closeElementSave={() => setShowTableSaveModal(false)}
          />
          {resultsComponent}
        </>
      )}
    </>
  )
}

ProjectSearchResults.propTypes = {
  searchResults: PropTypes.shape({
    results: PropTypes.array,
    count: PropTypes.number,
    current: PropTypes.number,
    related_count: PropTypes.object,
    next: PropTypes.string,
    previous: PropTypes.string,
    total_pages: PropTypes.number
  }),
  documentSearchResults: PropTypes.shape({
    results: PropTypes.array,
    count: PropTypes.number,
    current: PropTypes.number,
    related_count: PropTypes.object,
    next: PropTypes.string,
    previous: PropTypes.string,
    total_pages: PropTypes.number
  }),
  isPending: PropTypes.bool,
  error: PropTypes.bool,
  isSidebarVisible: PropTypes.bool,
  onPageChange: PropTypes.func,
  hasLocationPermission: PropTypes.bool,
  hasLocationSearchResults: PropTypes.bool,
  onExportToExcel: PropTypes.func,
  currentFilters: PropTypes.array,
  onGetResultUrl: PropTypes.func,
  resultHash: PropTypes.string,
  onMapImageDownload: PropTypes.func,
  isMapDownloadPending: PropTypes.bool,
  isExcelExportPending: PropTypes.bool
}

export default ProjectSearchResults
